import { Button, cn, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { FieldError } from '@/components/ui/field-error';
import { CUSTOM_TRADING_FEES_QUERY_KEY } from '@/hooks/queries/fees/use-fees';
import { getErrorMessage } from '@/lib/api/error';
import { buildSetCustomTradingFeeRequest, type CustomTradingFee, setCustomTradingFees, TRADING_FEE_PATTERN } from '@/services/api/fees';

// Validation, messages verbatim: required first, then the 4-decimal
// regex (negative values allowed — legacy parity).
const feeValueSchema = (requiredMessage: string) => z.string().min(1, requiredMessage).regex(TRADING_FEE_PATTERN, 'Maximum 4 decimal places allowed');

const customTradingFeeSchema = z.object({
  accountHolderId: z.string().min(1, 'Account Holder ID is required'),
  makerFeeSpot: feeValueSchema('Maker fee is required'),
  takerFeeSpot: feeValueSchema('Taker fee is required'),
  makerFeeFutures: feeValueSchema('Maker fee is required'),
  takerFeeFutures: feeValueSchema('Taker fee is required'),
});

type CustomTradingFeeSchema = z.infer<typeof customTradingFeeSchema>;

const ADD_MODE_DEFAULTS: CustomTradingFeeSchema = { accountHolderId: '', makerFeeSpot: '', takerFeeSpot: '', makerFeeFutures: '', takerFeeFutures: '' };

// Stored fees may be strings or numbers —
// String()-normalise them into the form.
const toFormValues = (fee: CustomTradingFee): CustomTradingFeeSchema => ({
  accountHolderId: fee.accountHolderId,
  makerFeeSpot: String(fee.makerFeeSpot),
  takerFeeSpot: String(fee.takerFeeSpot),
  makerFeeFutures: String(fee.makerFeeFutures),
  takerFeeFutures: String(fee.takerFeeFutures),
});

const FEE_FIELDS = [
  { name: 'makerFeeSpot', label: 'Maker Fee (SPOT)' },
  { name: 'takerFeeSpot', label: 'Taker Fee (SPOT)' },
  { name: 'makerFeeFutures', label: 'Maker Fee (FUTURES)' },
  { name: 'takerFeeFutures', label: 'Taker Fee (FUTURES)' },
] as const;

interface CustomTradingFeeDialogProps {
  /** null → add mode */
  fee: CustomTradingFee | null;
  onClose: () => void;
}

// Mounted only while open — closing by any means resets the form and exits edit
// mode.
export const CustomTradingFeeDialog = ({ fee, onClose }: CustomTradingFeeDialogProps) => {
  const fieldId = useId();
  const queryClient = useQueryClient();
  const isEditMode = fee !== null;
  const actionVerb = isEditMode ? 'Update' : 'Add';

  const form = useForm<CustomTradingFeeSchema>({
    resolver: zodResolver(customTradingFeeSchema),
    defaultValues: isEditMode ? toFormValues(fee) : ADD_MODE_DEFAULTS,
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    const accountHolderId = values.accountHolderId.trim();
    const justification = await confirmAction({
      title: `Confirm ${actionVerb} custom trading fee`,
      description: `Proceeding with this action will ${actionVerb.toLowerCase()} a custom trading fee for ${accountHolderId}. Your action here will be logged and will be auditable.`,
      actionLabel: `${actionVerb} custom trading fee`,
      onConfirm: async (comment) => {
        try {
          // Fees go as strings so "0" survives the lambda's falsy check
          await setCustomTradingFees(accountHolderId, buildSetCustomTradingFeeRequest(values, comment));
        } catch (error) {
          // Failure copy — rethrown so the audit dialog stays open showing it
          throw new Error(`Error updating fees: ${getErrorMessage(error)}`);
        }
      },
    });
    if (justification === null) return;

    toast.success(`Successfully ${isEditMode ? 'updated' : 'added'} custom trading fee for ${accountHolderId}`);
    await queryClient.invalidateQueries({ queryKey: CUSTOM_TRADING_FEES_QUERY_KEY });
    onClose();
  });

  const idFieldError = form.formState.errors.accountHolderId;

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="size-5" />
            {isEditMode ? 'Edit Custom Trading Fee' : 'New Custom Trading Fee'}
          </DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-account`}>Account Holder ID</Label>
            {/* The endpoint is an upsert keyed by the PATH id —
                legacy left this editable in edit mode, silently creating a NEW record
                and orphaning the old one. Read-only when editing. */}
            <Input
              aria-invalid={idFieldError ? true : undefined}
              className={cn(idFieldError && 'border-red-500 focus-visible:ring-red-500', isEditMode && 'cursor-not-allowed bg-muted/60 text-muted-foreground')}
              id={`${fieldId}-account`}
              readOnly={isEditMode}
              {...form.register('accountHolderId')}
            />
            {/* An empty id shows a field error with NO
                message — the red ring above is the only visual signal. */}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {FEE_FIELDS.map(({ name, label }) => (
              <div className="flex flex-col gap-1.5" key={name}>
                <Label htmlFor={`${fieldId}-${name}`}>{label}</Label>
                <div className="relative">
                  <Input className="pr-7" id={`${fieldId}-${name}`} inputMode="decimal" {...form.register(name)} />
                  <span aria-hidden className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                    %
                  </span>
                </div>
                <FieldError message={form.formState.errors[name]?.message} />
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Update
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
CustomTradingFeeDialog.displayName = 'CustomTradingFeeDialog';
